import React from 'react';
import ReactDOM from 'react-dom/client';
import 'antd/dist/reset.css';
import './index.css';
import App from './App';

// Suppress benign ResizeObserver loop errors from some UI components
if (typeof window !== 'undefined') {
  const isResizeObserverMessage = (msg) => {
    if (!msg) return false;
    const text =
      typeof msg === 'string'
        ? msg
        : msg instanceof Error
        ? msg.message
        : typeof msg === 'object' && msg.message
        ? msg.message
        : '';
    return (
      typeof text === 'string' &&
      (text === 'ResizeObserver loop limit exceeded' ||
        text === 'ResizeObserver loop completed with undelivered notifications.' ||
        text.includes('ResizeObserver loop'))
    );
  };

  // Tame React error overlay for ResizeObserver noise
  const reo = window.__REACT_ERROR_OVERLAY_GLOBAL_HOOK__ || {};
  window.__REACT_ERROR_OVERLAY_GLOBAL_HOOK__ = reo;
  const origReportError = reo.reportError;
  reo.reportError = (err) => {
    if (isResizeObserverMessage(err?.message || err?.reason || err)) return;
    if (typeof origReportError === 'function') return origReportError(err);
  };
  const origDismissError = reo.dismissError;
  reo.dismissError = (err) => {
    if (isResizeObserverMessage(err?.message || err?.reason || err)) return;
    if (typeof origDismissError === 'function') return origDismissError(err);
  };

  const suppressResizeObserverError = (event) => {
    const msg = event?.message || event?.reason?.message || event?.error?.message || '';
    if (isResizeObserverMessage(msg)) {
      if (typeof event.preventDefault === 'function') event.preventDefault();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
      return true;
    }
    return false;
  };

  const handleGlobalError = (message, source, lineno, colno, error) => {
    const msg = message || error?.message || (typeof error === 'string' ? error : '') || '';
    if (isResizeObserverMessage(msg)) {
      return true; // swallow
    }
    return false;
  };
  // Also silence console.error for this specific noisy message in dev overlays
  const originalConsoleError = console.error; // eslint-disable-line no-console
  console.error = (...args) => {
    const first = args && args[0];
    if (isResizeObserverMessage(first)) {
      return;
    }
    originalConsoleError(...args);
  };
  // Capture phase to beat React error overlay
  window.addEventListener('error', suppressResizeObserverError, true);
  window.addEventListener(
    'unhandledrejection',
    (event) => {
      if (suppressResizeObserverError(event)) {
        event.preventDefault();
      }
    },
    true,
  );
  // Fallback global handlers
  const prevOnError = window.onerror;
  window.onerror = (...args) => {
    const swallowed = handleGlobalError(...args);
    if (swallowed) return true;
    if (typeof prevOnError === 'function') {
      return prevOnError(...args);
    }
    return false;
  };
  const prevOnUnhandled = window.onunhandledrejection;
  window.onunhandledrejection = (event) => {
    const msg = event?.reason?.message || event?.reason || '';
    if (typeof msg === 'string' && msg.includes('ResizeObserver loop')) {
      event.preventDefault();
      return true;
    }
    if (typeof prevOnUnhandled === 'function') {
      return prevOnUnhandled(event);
    }
    return false;
  };
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
